import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import {
  GraphemeSvgGlyph,
  GraphemeSvgGlyphPlayer,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg";
import { NewMorphemeLessonLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._context";
import { useLessonPageStrokeViewSize } from "@-/dev.improve.haniverse.routes.nav.app.learn.lesson.new-morpheme._lib.stroke-view-size";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useMorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMemo, useState } from "react";
import { View } from "@-/com.npmjs.react-native";
import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

export function LessonPageGlyphBlock({
  isActive = true,
  replayKey = 0,
}: {
  isActive?: boolean;
  replayKey?: number;
} = {}) {
  const [grapheme, morphemeMnemonicsPanelMorphemeSlice, lessonPart, pronunciationPinyin = ""] =
    useContextSelectors(
      NewMorphemeLessonLayoutContext,
      (c) => c.grapheme,
      (c) => c.morphemeMnemonicsPanelMorphemeSlice.data,
      (c) => c.lessonPart,
      (c) => c.onlyPinyin ?? c.introducedPinyinList[0],
    );
  // A "New Pronunciation" card already taught the character's strokes, so it shows the glyph
  // still, in the tone colour of the reading it introduces.
  const isPronunciation = lessonPart === "pronunciation";
  const { color: toneColor } = useMorphemeTranscription(pronunciationPinyin ?? "");
  const pronunciationColor = isPronunciation ? (toneColor ?? undefined) : undefined;
  const { frontVisibleSignal } = useLearnCardSurface();
  const [displayMode] = useContextSelectors(NavContext, (c) => c.characterDisplayMode);
  const preferredSize = useLessonPageStrokeViewSize();
  const [availableSize, setAvailableSize] = useState<number | null>(null);
  const strokeViewSize = availableSize ?? preferredSize;
  const getGraphemeGlyph = useGetGraphemeGlyph();
  const graphemeGlyph = useMemo(
    () => getGraphemeGlyph(grapheme, displayMode === "font" ? { uncolored: true } : {}),
    [displayMode, getGraphemeGlyph, grapheme],
  );
  const sliceMatchesGrapheme = morphemeMnemonicsPanelMorphemeSlice.grapheme === grapheme;
  const { graphemeStrokes } = morphemeMnemonicsPanelMorphemeSlice;
  // `null` (no stroke data) makes the player render the font glyph instead.
  const svgStrokeList = useMemo(
    () =>
      graphemeStrokes === null
        ? null
        : graphemeStrokes.map(({ median, path }) => ({ median, path })),
    [graphemeStrokes],
  );
  return (
    <View
      className="min-h-0 flex-1 items-center justify-center self-stretch"
      onLayout={({ nativeEvent: { layout } }) => {
        setAvailableSize(Math.max(1, Math.min(layout.width, layout.height)));
      }}
    >
      {/* Let glyph taps reach the card's reveal target. */}
      <View
        className="z-10 shrink-0 items-center justify-center"
        pointerEvents="none"
        style={{
          height: strokeViewSize,
          width: strokeViewSize,
        }}
      >
        {displayMode === "svg" && sliceMatchesGrapheme && isPronunciation ? (
          <GraphemeSvgGlyph
            color={pronunciationColor}
            glyph={graphemeGlyph}
            showBackground={false}
            size={strokeViewSize}
            strokeList={svgStrokeList}
          />
        ) : displayMode === "svg" && sliceMatchesGrapheme ? (
          <GraphemeSvgGlyphPlayer
            autoPlay={isActive}
            glyph={graphemeGlyph}
            loop={false}
            playSignal={frontVisibleSignal}
            replayKey={replayKey}
            showBackground={false}
            showStrokeControls={false}
            size={strokeViewSize}
            strokeList={svgStrokeList}
          />
        ) : (
          <GraphemeFontGlyph
            className="text-foreground"
            glyph={graphemeGlyph}
            style={{
              color: pronunciationColor,
              fontSize: Math.round(strokeViewSize * 0.62),
              lineHeight: Math.round(strokeViewSize * 0.62 * 1.2),
            }}
          />
        )}
      </View>
    </View>
  );
}
